// IST-pinned date helpers. The backend stores `tests.scheduled_date` as
// a Postgres DATE (calendar date, no TZ); the pg driver, with the server's
// process TZ set to Asia/Kolkata, serialises that into JSON as e.g.
//   "2026-05-26T18:30:00.000Z"   (IST midnight 2026-05-27)
// Anything that then does `.slice(0, 10)` reads the UTC date portion and
// shows the wrong day.
//
// These helpers previously relied on JS Date's local accessors (getFullYear
// / getMonth / getDate) and toLocaleDateString's default device tz. That
// works on phones with TZ=IST and a properly localised WebView — but some
// Indian-OEM Android WebViews ship locale data that ignores the device tz
// for en-IN renders and emits the UTC portion of the timestamp in ISO
// format. Teachers on those phones saw "2026-06-01" on a card whose edit
// modal correctly pre-selected Jun 2.
//
// The fix is to pin every formatter and every date accessor to
// Asia/Kolkata explicitly, so we never depend on whatever the device
// happens to think its timezone is. Server side already runs in IST, so
// the calendar day is consistent end-to-end.

const IST = "Asia/Kolkata";

// "YYYY-MM-DD" in IST regardless of device tz. en-CA's date format is
// always YYYY-MM-DD, so this is also safe to feed to <input type="date">.
function istCalendarDate(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit",
    timeZone: IST,
  }).format(d);
}

// "27 May 2026" — IST calendar date, human-friendly. Returns "" for
// nullish/invalid input so callers can short-circuit with `&&`.
export function formatLocalDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    timeZone: IST,
  });
}

// "2026-05-27" — what an <input type="date"> wants. IST calendar date, so
// editing a 2026-05-27 IST test pre-selects 2026-05-27 on every device
// regardless of its local tz or WebView locale.
export function localDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return istCalendarDate(d);
}

// Today's calendar date in IST as "YYYY-MM-DD". Used by callers that need
// to bucket "live vs upcoming" without relying on device-tz `new Date()`
// components.
export function istTodayKey(): string {
  return istCalendarDate(new Date());
}

// "YYYY-MM-DD" key for an ISO timestamp in IST. String-lex order matches
// calendar order, so callers compare with `<`/`>` directly. Returns null
// for nullish/invalid input.
export function istDayKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return istCalendarDate(d);
}

// "3d 11h" / "23h 5m" / "12m" / null. Null means either invalid input or
// the deadline has passed (the chip caller hides itself then). Reads the
// difference live each call — wrap in a setInterval to tick.
export function formatCountdown(iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (isNaN(target)) return null;
  const ms = target - now;
  if (ms <= 0) return null;
  const d = Math.floor(ms / (24 * 3600_000));
  const h = Math.floor((ms % (24 * 3600_000)) / 3600_000);
  const m = Math.floor((ms % 3600_000) / 60_000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// True when the (chip-call) result should adopt the urgent (amber/red) tint
// — within the last hour before the deadline.
export function isCountdownUrgent(iso: string | null | undefined, now = Date.now()): boolean {
  if (!iso) return false;
  const target = new Date(iso).getTime();
  if (isNaN(target)) return false;
  const ms = target - now;
  return ms > 0 && ms < 60 * 60_000;
}
